import React from 'react';
import { Clock, Leaf, Wheat, Heart } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const AboutPage = () => {
  const { t, currentLang } = useLanguage();
  const isRTL = currentLang === 'ar' || currentLang === 'ku';

  const values = [
    { 
      icon: Heart, 
      title: currentLang === 'ar' ? 'الشغف' : currentLang === 'de' ? 'Leidenschaft' : 'Passion', 
      desc: currentLang === 'ar' ? 'نصنع كل رغيف بحب وعناية' : currentLang === 'de' ? 'Wir backen jedes Brot mit Liebe' : 'We bake every loaf with love'
    },
    { 
      icon: Wheat, 
      title: currentLang === 'ar' ? 'الجودة' : currentLang === 'de' ? 'Qualität' : 'Quality', 
      desc: currentLang === 'ar' ? 'أعلى معايير الجودة الأوروبية' : currentLang === 'de' ? 'Höchste europäische Qualitätsstandards' : 'Highest European quality standards'
    },
    { 
      icon: Clock, 
      title: currentLang === 'ar' ? 'التراث' : currentLang === 'de' ? 'Erbe' : 'Heritage', 
      desc: currentLang === 'ar' ? 'وصفات متوارثة عبر الأجيال' : currentLang === 'de' ? 'Über Generationen vererbte Rezepte' : 'Recipes passed down through generations'
    },
    { 
      icon: Leaf, 
      title: currentLang === 'ar' ? 'الطبيعة' : currentLang === 'de' ? 'Natürlich' : 'Natural', 
      desc: currentLang === 'ar' ? 'مكونات طبيعية 100%' : currentLang === 'de' ? '100% natürliche Zutaten' : '100% natural ingredients'
    }
  ];

  return (
    <div className="min-h-screen bg-[#faf8f5] pt-24">
      {/* Page Header */}
      <div className="bg-[#0d4023] py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className={`max-w-2xl ${isRTL ? 'ms-auto text-end' : 'me-auto text-start'}`}>
            <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#b89759] block mb-4">
              {currentLang === 'ar' ? 'قصتنا' : currentLang === 'de' ? 'Unsere Geschichte' : 'Our Story'}
            </span>
            <h1 
              className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 tracking-tight"
              data-testid="about-title"
            >
              {t.about?.title || 'من نحن'}
            </h1>
            <p className="text-lg text-white/70 leading-relaxed">
              {t.about?.subtitle || 'قصة شغف وتراث عريق في قلب ألمانيا'}
            </p>
          </div>
        </div>
      </div>

      {/* Story Section */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Images Grid */}
            <div className="grid grid-cols-12 gap-4">
              <div className="col-span-7 space-y-4">
                <div className="overflow-hidden rounded-2xl">
                  <img 
                    src="https://images.unsplash.com/photo-1604908552986-eb22824b2150?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwyfHxiYWtlcnklMjBpbnRlcmlvcnxlbnwwfHx8fDE3ODgxMTAzOTF8MA&ixlib=rb-4.1.0&q=85"
                    alt="Bakery Interior"
                    className="w-full aspect-[4/5] object-cover hover:scale-105 transition-transform duration-700"
                  />
                </div>
              </div>
              <div className="col-span-5 space-y-4 pt-12">
                <div className="overflow-hidden rounded-2xl">
                  <img 
                    src="https://images.pexels.com/photos/5323489/pexels-photo-5323489.jpeg"
                    alt="Fresh Baklava"
                    className="w-full aspect-square object-cover hover:scale-105 transition-transform duration-700"
                  />
                </div>
                <div className="overflow-hidden rounded-2xl bg-[#0d4023] p-6 flex items-center justify-center">
                  <div className="text-center">
                    <span className="font-heading text-5xl font-bold text-[#b89759]">2020</span>
                    <p className="text-white/70 text-sm mt-2">
                      {currentLang === 'ar' ? 'منذ' : currentLang === 'de' ? 'Seit' : 'Since'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className={`space-y-8 ${isRTL ? 'text-end' : 'text-start'}`}>
              <div>
                <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#b89759] block mb-4">
                  {t.about?.storyTitle || 'قصتنا مع الخبز'}
                </span>
                <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-[#0d4023] tracking-tight mb-6">
                  {currentLang === 'ar' ? 'تراث الشام في قلب ألمانيا' 
                    : currentLang === 'de' ? 'Levantinisches Erbe im Herzen Deutschlands' 
                    : 'Levantine Heritage in the Heart of Germany'}
                </h2>
              </div>
              
              <div className="space-y-6 text-[#5a7062] leading-relaxed">
                <p className="text-lg">
                  {t.about?.storyText || 
                    'في عام 2020، بدأت رحلتنا بحلم بسيط: جلب نكهة وتقاليد الخبز الشامي الأصيل إلى ألمانيا. نحن لسنا مجرد مخبز، بل جسر يربط بين ثقافتين عريقتين.'}
                </p>
                <p>
                  {currentLang === 'ar' 
                    ? 'كل يوم نستيقظ قبل الفجر لنخبز لكم أفضل المنتجات الطازجة، مستخدمين نفس الوصفات التي توارثناها من أجدادنا في دمشق.'
                    : currentLang === 'de'
                    ? 'Jeden Tag wachen wir vor Sonnenaufgang auf, um die frischesten Produkte zu backen, mit denselben Rezepten, die wir von unseren Vorfahren in Damaskus geerbt haben.'
                    : 'Every day we wake up before dawn to bake the freshest products, using the same recipes we inherited from our ancestors in Damascus.'}
                </p>
              </div>

              {/* Stats */}
              <div className={`flex gap-8 pt-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <div>
                  <span className="font-heading text-4xl font-bold text-[#0d4023]">1000+</span>
                  <p className="text-[#5a7062] text-sm">
                    {currentLang === 'ar' ? 'عميل سعيد' : currentLang === 'de' ? 'Zufriedene Kunden' : 'Happy Customers'}
                  </p>
                </div>
                <div className="w-px bg-[#b89759]/30" />
                <div>
                  <span className="font-heading text-4xl font-bold text-[#0d4023]">20+</span>
                  <p className="text-[#5a7062] text-sm">
                    {currentLang === 'ar' ? 'منتج طازج' : currentLang === 'de' ? 'Frische Produkte' : 'Fresh Products'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 lg:py-28 bg-[#0d4023]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#b89759] block mb-4">
              {currentLang === 'ar' ? 'قيمنا' : currentLang === 'de' ? 'Unsere Werte' : 'Our Values'}
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white tracking-tight">
              {currentLang === 'ar' ? 'ما يميزنا' : currentLang === 'de' ? 'Was uns auszeichnet' : 'What Sets Us Apart'}
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, i) => (
              <div 
                key={i} 
                className="group p-8 rounded-2xl border border-white/10 hover:border-[#b89759]/50 hover:bg-white/5 transition-all duration-300"
                data-testid={`value-card-${i}`}
              >
                <div className="w-14 h-14 rounded-full bg-[#b89759]/10 flex items-center justify-center text-[#b89759] mb-6 group-hover:bg-[#b89759] group-hover:text-[#0d4023] transition-colors duration-300">
                  <value.icon className="w-7 h-7" />
                </div>
                <h3 className="font-heading text-xl font-bold text-white mb-2">
                  {value.title}
                </h3>
                <p className="text-white/60 text-sm leading-relaxed">{value.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team/Promise Section */}
      <section className="py-20 lg:py-28 bg-[#faf8f5]">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#b89759] block mb-4">
            {currentLang === 'ar' ? 'وعدنا' : currentLang === 'de' ? 'Unser Versprechen' : 'Our Promise'}
          </span>
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#0d4023] tracking-tight mb-6">
            {currentLang === 'ar' ? 'جودة لا تُساوَم' 
              : currentLang === 'de' ? 'Kompromisslose Qualität' 
              : 'Uncompromising Quality'}
          </h2>
          <p className="text-lg text-[#5a7062] leading-relaxed mb-10 max-w-2xl mx-auto">
            {currentLang === 'ar' 
              ? 'نلتزم بتقديم أفضل المنتجات الطازجة يومياً، مع الحفاظ على الطعم الأصيل والجودة العالية التي يستحقها عملاؤنا.'
              : currentLang === 'de'
              ? 'Wir verpflichten uns, täglich die besten frischen Produkte zu liefern und dabei den authentischen Geschmack und die hohe Qualität zu bewahren, die unsere Kunden verdienen.'
              : 'We commit to delivering the best fresh products daily, while preserving the authentic taste and high quality our customers deserve.'}
          </p>
          <a
            href="/contact"
            className="inline-flex items-center gap-2 px-8 py-3 bg-[#0d4023] text-[#faf8f5] rounded-full font-medium hover:bg-[#1a5c32] transition-colors duration-300"
            data-testid="about-contact-cta"
          >
            {currentLang === 'ar' ? 'زورونا اليوم' : currentLang === 'de' ? 'Besuchen Sie uns heute' : 'Visit Us Today'}
          </a>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
